import { useState, useEffect } from 'react';
import { useMappingsStore } from '../store/mappings.store';
import { useCrmStore } from '../store/crm.store';
import { PathHop, Locator } from '@/features/application/types/forms';

export interface UsePathBuilderProps {
  path: PathHop[];
  onPathChange: (newPath: PathHop[]) => void;
}

export const usePathBuilder = ({ path, onPathChange }: UsePathBuilderProps) => {
  const { getCurrentPrimaryObject } = useMappingsStore();
  const {
    fetchCrmObjectRelationships,
    getRelationshipsForObject,
    isLoadingRelationshipsForObject,
  } = useCrmStore();

  const primaryObject = getCurrentPrimaryObject();
  const [openComboboxIndex, setOpenComboboxIndex] = useState<number | null>(null);
  const [isAddingStep, setIsAddingStep] = useState(false);

  // Ensure path always starts with anchor hop
  useEffect(() => {
    if (primaryObject && (path.length === 0 || path[0].object !== primaryObject)) {
      const anchorHop: PathHop = {
        object: primaryObject,
        kind: 'anchor',
      };
      onPathChange([anchorHop]);
    }
  }, [primaryObject, path, onPathChange]);

  // Fetch relationships for all objects in the path
  useEffect(() => {
    if (!primaryObject) return;

    // Always fetch for primary object
    fetchCrmObjectRelationships(primaryObject);

    // Fetch for each object in the path
    path.forEach((hop) => {
      fetchCrmObjectRelationships(hop.object);
    });
  }, [path, primaryObject, fetchCrmObjectRelationships]);

  // Handle changing a hop (relationship selection)
  const handleHopChange = (index: number, selectedRelationshipName: string) => {
    if (!selectedRelationshipName) return;

    const parentHop = path[index - 1];
    if (!parentHop) return;

    const relationships = getRelationshipsForObject(parentHop.object);
    if (!relationships) return;

    const allRelations = [...relationships.parents, ...relationships.children];
    const relation = allRelations.find((rel) => rel.value === selectedRelationshipName);
    if (!relation) return;

    const newHop: PathHop = {
      object: relation.objectType,
      rel: selectedRelationshipName,
      kind: relation.type === 'child' ? 'child' : 'lookup',
      ...(relation.type === 'child' && { locator: null }),
    };

    // Update the path by replacing from this index onward
    const newPath = [...path.slice(0, index), newHop];
    onPathChange(newPath);
    setOpenComboboxIndex(null);

    // Only exit add step mode if we were actually adding a step
    if (isAddingStep && index >= path.length) {
      setIsAddingStep(false);
    }
  };

  // Handle adding a new step to the path
  const handleAddStep = () => {
    setIsAddingStep(true);

    // Open the newly added combobox after the state update
    setOpenComboboxIndex(path.length);
  };

  // Handle removing a step from the path
  const handleRemoveStep = (index: number) => {
    if (index <= 0) return; // Can't remove anchor hop

    const newPath = path.slice(0, index);
    onPathChange(newPath);
    setOpenComboboxIndex(null);
    setIsAddingStep(false);
  };

  // Handle locator change for a specific path hop
  const handleLocatorChange = (index: number, newLocator: Locator | null) => {
    if (index >= path.length) return;

    const newPath = [...path];
    newPath[index] = {
      ...newPath[index],
      locator: newLocator,
    };
    onPathChange(newPath);
    setIsAddingStep(false);
  };

  // Get relationship options for a given parent object
  const getRelationshipOptions = (parentObject: string) => {
    const relationships = getRelationshipsForObject(parentObject);
    const isLoading = isLoadingRelationshipsForObject(parentObject);

    const allPossibleNextObjects = [
      ...(relationships?.children.map((obj) => ({
        value: obj.value,
        label: obj.label.includes('(') ? obj.label.split('(')[0].trim() : obj.objectType,
        relationshipType: 'child' as const,
      })) || []),
      ...(relationships?.parents.map((obj) => ({
        value: obj.value,
        label: obj.label.includes('(') ? obj.label.split('(')[0].trim() : obj.objectType,
        relationshipType: 'parent' as const,
      })) || []),
    ];

    return { allPossibleNextObjects, isLoading };
  };

  // Handle combobox open/close state
  const handleComboboxOpenChange = (index: number, open: boolean) => {
    if (!open) setOpenComboboxIndex(null);
    else setOpenComboboxIndex(index);
  };

  // Check if we can add more steps
  const canAddMoreSteps = () => {
    if (path.length === 0) return false;

    const lastHop = path[path.length - 1];
    const relationships = getRelationshipsForObject(lastHop.object);

    if (!relationships) return false;

    const hasAvailableRelationships =
      relationships.parents.length > 0 || relationships.children.length > 0;

    return hasAvailableRelationships;
  };

  // Check if we're currently resolving the next step
  const isResolvingNextStep = () => {
    if (path.length === 0) return false;

    const lastHop = path[path.length - 1];
    return isLoadingRelationshipsForObject(lastHop.object);
  };

  return {
    primaryObject,
    openComboboxIndex,
    handleHopChange,
    handleAddStep,
    handleRemoveStep,
    handleLocatorChange,
    getRelationshipOptions,
    handleComboboxOpenChange,
    canAddMoreSteps,
    isResolvingNextStep,
    isAddingStep,
    setIsAddingStep,
  };
};
